import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { WorkflowEditor } from "../components/workflow/WorkflowEditor";
import { apiService } from "../services/apiService";
import { Workflow, BackendWorkflow } from "../types";
import toast from "../utils/toast";
import { useTranslation } from "react-i18next";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

interface WorkflowDesignerProps {
  readonly?: boolean;
}

const NAV_REFRESH_EVENT = "nav:refresh";

export const WorkflowDesigner: React.FC<WorkflowDesignerProps> = ({
  readonly = false,
}) => {
  const { t } = useTranslation(["workflows", "errors", "common"]);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const workflowId = id ? parseInt(id) : undefined;

  // Get query parameters for user-specific workflows
  const urlParams = new URLSearchParams(window.location.search);
  const projectId = urlParams.get("projectId");
  const userId = urlParams.get("userId");
  const userName = urlParams.get("userName");
  const workflowTypeParam = urlParams.get("workflowType") as
    | "PROJECT"
    | "TASK"
    | null;
  const [workflow, setWorkflow] = useState<Workflow | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const loadWorkflow = useCallback(async () => {
    if (!workflowId) return;

    try {
      setLoading(true);
      const response = await apiService.getWorkflowById(workflowId);
      setWorkflow(response);
    } catch (error) {
      toast.error(t("errors:loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [workflowId]);

  useEffect(() => {
    if (workflowId) {
      loadWorkflow();
    }
  }, [workflowId, loadWorkflow]);

  const handleSaveWorkflow = async (workflowData: Partial<BackendWorkflow>) => {
    try {

      setLoading(true);
      let savedWorkflow: Workflow;

      // Add project context to workflow data
      const enhancedWorkflowData = {
        ...workflowData,
        projectId: projectId
          ? parseInt(projectId)
          : workflow?.workflowContainerId,
        name: workflowData.name || `Project Workflow`,
      };

      if (workflowId) {
        savedWorkflow = await apiService.updateWorkflow(
          workflowId,
          enhancedWorkflowData
        );
      } else {
        // Create new workflow
        const workflowToCreate = {
          ...enhancedWorkflowData,
        };
        savedWorkflow = await apiService.createWorkflow(workflowToCreate);
      }
      setWorkflow(savedWorkflow);
      toast.success(
        workflowId
          ? t("workflows:workflowUpdated")
          : t("workflows:workflowCreated")
      );
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent(NAV_REFRESH_EVENT, { detail: { workflows: true } })
        );
      }

      // Reload workflow data to ensure UI is synchronized with backend
      if (workflowId && savedWorkflow.id) {
        // Force reload the workflow from backend to get the latest state
        setTimeout(async () => {
          try {
            const reloadedWorkflow = await apiService.getWorkflowById(
              savedWorkflow.id
            );
            setWorkflow(reloadedWorkflow);
          } catch (reloadError) {
          }
        }, 500);
      }

      // Navigate to edit mode if this was a new workflow
      if (!workflowId && savedWorkflow.id) {
        navigate(`/workflows/${savedWorkflow.id}/edit`);
      }
    } catch (error) {
      toast.error(t("errors:saveFailed"));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            {/* Geri Butonu */}
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center text-slate-600 hover:text-slate-900 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>

            {/* Workflow İsmi veya Başlık */}
            <h1 className="text-xl font-semibold text-gray-900">
              {workflow?.name ||
                (readonly
                  ? t("workflows:viewWorkflow")
                  : workflowId
                  ? t("workflows:editWorkflow")
                  : t("workflows:createWorkflow"))}
            </h1>

            {userName && (
              <p className="text-sm text-blue-600">
                👤 {t("workflows:creatingWorkflowFor", { user: userName })}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {projectId && (
              <p className="text-sm text-gray-500">
                {t("projects:projectId")}: {projectId}
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        <WorkflowEditor
          workflow={workflow}
          onSave={handleSaveWorkflow}
          readonly={readonly}
          onSaveSuccess={() => {
          }}
        />
      </div>
    </div>
  );
};
